<template>
    <div dusk="NoteEditor" class="flex items-stretch gap-2.5 py-3">
        <div class="w-[3px] shrink-0 rounded-sm" :style="{ backgroundColor: selectedColor }"></div>

        <div class="min-w-0 flex-1">
            <!-- The input decides whether it renders the plain textarea or the rich
                 editor; either way its bottom-right corner is left to the dictation
                 button, so a long note never runs underneath it, and Esc closes the
                 editor from both. -->
            <RichTextInput
                ref="noteField"
                v-model="annotation"
                :rich="richTextEditorIsEnabled"
                dusk="noteTextarea"
                :aria-label="localizations[lang].writeNote"
                :placeholder="localizations[lang].writeNote"
                @keydown.native.esc.stop="closeNoteEditor()"
            >
                <!-- Dictation composes plain sentences. Handing it an empty text makes it
                     emit only the sentence it just finished, which the input appends as
                     text in whichever editor renders, instead of a string glued onto the
                     note's markup. -->
                <template v-if="isDesktopBrowser" #adornment>
                    <ComponentSpeechToText
                        text=""
                        :speech-to-text-lang="speechToTextLang"
                        class="absolute bottom-0 right-1 [&>div]:my-1"
                        @update:text="appendDictation($event)"
                    />
                </template>
            </RichTextInput>

            <!-- The passage reads as the quote it is: the note above it is the point. -->
            <button
                v-if="quotePreview"
                type="button"
                class="mt-2 block w-full cursor-pointer break-words border-none bg-transparent p-0 text-left text-xs italic leading-snug text-theme-toolbar-icon focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                :title="localizations[lang].clickToToggle"
                @click="isExpanded = !isExpanded"
            >
                {{ quotePreview }}
                <span v-if="pageLabel" class="whitespace-nowrap not-italic opacity-70"> · {{ pageLabel }}</span>
            </button>

            <!-- One row for everything that commits the note: how it will look, then
                 what to do with it. Wraps when the panel is too narrow. -->
            <div class="mt-4 flex flex-wrap items-center gap-x-2 gap-y-3 [container-type:inline-size]">
                <!-- pr-1 keeps the highlighter form's focus ring clear of the panel's overflow -->
                <HighlighterColorPicker
                    class="note-commit-row__colors flex-1 pr-1"
                    variant="standard"
                    :selected-slug="selectedColorSlug"
                    @select="selectedColorSlug = $event.slug"
                    @form-toggle="isHighlighterFormOpen = $event"
                />
                <!-- The highlighter form brings its own Cancel/Save, so the note's pair
                     hides while the form holds the row. -->
                <transition name="fade-transition">
                    <CommitActions
                        v-if="!isHighlighterFormOpen"
                        class="ml-auto"
                        save-dusk="SaveNote"
                        :save-label="localizations[lang].save"
                        :is-save-disabled="!canSave || isSaving"
                        @cancel="closeNoteEditor()"
                        @save="save"
                    />
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import CommitActions from '../../Components/buttons/CommitActions.vue';
import ComponentSpeechToText from '../../ReaderSelectionTool/ComponentSpeechToText.vue';
import HighlighterColorPicker from '../../HighlighterColorPicker/HighlighterColorPicker.vue';
import RichTextInput from '../../Components/RichTextInput.vue';
import { clipToPreviewWords } from '../../../js/utils/notePreviewText.js';
import plainTextToEditorHtml from '../../../js/tiptap/plainTextToEditorHtml.js';

/**
 * A PDF stores a note through the text layer of its page, which is only mounted
 * while that page is on screen. How long the save waits for the navigation to
 * mount it, and how often it looks.
 */
const PAGE_MOUNT_TIMEOUT_MS = 1000;
const PAGE_MOUNT_POLL_MS = 50;

export default {
    name: 'NoteEditor',

    components: {
        CommitActions,
        ComponentSpeechToText,
        HighlighterColorPicker,
        RichTextInput,
    },

    props: {
        /**
         * The note being edited. Left null while writing a new one, where the
         * passage comes from the selection the reader just made.
         */
        selection: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            annotation: '',
            selectedColorSlug: '',
            isExpanded: false,
            /** True while the highlighter form has the commit row. */
            isHighlighterFormOpen: false,
            /** True while a save waits for its page to mount: a second Save must not emit twice. */
            isSaving: false,
            /**
             * Snapshot, not a live read: clicking the page clears `currentHighlight`,
             * and the note being written has to outlive that.
             */
            target: null,
        };
    },

    computed: {
        ...mapState(['ui', 'localizations', 'lang', 'currentHighlight']),
        ...mapGetters(['isPdf', 'isDesktopBrowser', 'allHighlighters', 'richTextEditorIsEnabled', 'onlyMobileVisible']),

        selectedColor() {
            return this.allHighlighters.find(color => color.slug === this.selectedColorSlug)?.color || '';
        },

        quotePreview() {
            const passage = this.target?.totalText || '';
            return this.isExpanded ? passage : clipToPreviewWords(passage);
        },

        /**
         * The page the note belongs to: a stored note carries its own, a new one takes
         * the page the selection was made on.
         */
        page() {
            return this.target?.startPage ?? this.ui.selectionTool.page;
        },

        pageLabel() {
            if (!this.isPdf || !this.page) return '';
            return `${this.localizations[this.lang].page} ${this.page}`;
        },

        /**
         * The note this instance is the editor of, matching `ui.noteEditor.uuid`;
         * null while a new note is being written.
         */
        editedNoteUuid() {
            return this.selection?.uuid ?? null;
        },

        canSave() {
            return this.annotation.trim().length > 0 && this.selectedColorSlug.length > 0;
        },

        /**
         * What the save declares about `annotation`: the rich editor emits markup for
         * the host to sanitize, the plain textarea never marks its text.
         */
        annotationFormat() {
            return this.richTextEditorIsEnabled ? 'html' : undefined;
        },

        /**
         * A stored note tapped on a phone is opened to be read: taking the caret would
         * raise the keyboard over it. A new note is opened to be written, so it keeps it.
         */
        shouldFocusFieldOnMount() {
            return !(this.onlyMobileVisible && this.editedNoteUuid);
        },

        speechToTextLang() {
            return { es: 'es-AR', en: 'en-US', pt: 'pt-BR' }[this.lang];
        },
    },

    created() {
        // The editor is a text field: the reader's own arrow keys must not page the
        // book while the reader is typing into it.
        this.pauseKeyboardNavigation('noteEditor');
        this.target = this.selection || this.currentHighlight;
        this.annotation = this.annotationForEditor(this.target);
        this.selectedColorSlug = this.target?.colorSlug || this.allHighlighters?.[0]?.slug || '';
    },

    mounted() {
        // The note being edited can sit anywhere in the list: bring it into view, and
        // take the caret without scrolling again on top of that.
        this.$nextTick(() => {
            this.$el?.scrollIntoView?.({ block: 'nearest' });
            if (this.shouldFocusFieldOnMount && typeof this.$refs.noteField?.focus === 'function') {
                this.$refs.noteField.focus({ preventScroll: true });
            }
        });
    },

    /**
     * This component being mounted is what "the editor is open" means, so its
     * teardown owns both halves of that: the reader gets its keys back, and the flag
     * that would remount the editor is cleared. Closing the notebook — which unmounts
     * it without going through cancel — is the case that needs it.
     *
     * Unless the editor simply moved to another note, which unmounts this instance
     * while the state still says open: that instance owns the state and the reader's
     * keys now, and clearing either here would close the editor the reader just asked
     * for.
     */
    beforeDestroy() {
        if (this.ui.noteEditor.opened && this.ui.noteEditor.uuid !== this.editedNoteUuid) return;

        this.resumeKeyboardNavigation('noteEditor');
        this.closeNoteEditor();
    },

    methods: {
        ...mapMutations(['closeNoteEditor', 'openToast', 'pauseKeyboardNavigation', 'resumeKeyboardNavigation']),
        ...mapActions(['goToPage', 'updateStoredHighlight']),

        async save() {
            if (!this.canSave || this.isSaving) return;
            this.isSaving = true;

            if (this.isPdf && this.target?.isStored?.()) {
                await this.saveStoredNote();
                return;
            }

            if (this.isPdf && this.page && !(await this.ensurePageTextLayerIsMounted(this.page))) {
                this.isSaving = false;
                this.openToast({ text: this.localizations[this.lang].highlightCreationFailed });
                return;
            }

            // The captured target travels with the event: the viewer's own selection
            // may have moved on while the note was being written.
            this.$bus.$emit(
                'applyColorAndConfirmSelection',
                {
                    color: this.selectedColor,
                    colorSlug: this.selectedColorSlug,
                    page: this.page,
                    annotationText: this.annotation,
                    annotationFormat: this.annotationFormat,
                    hasAnnotation: true,
                    needUpdated: Boolean(this.target?.isStored?.()),
                },
                this.target
            );

            this.closeNoteEditor();
        },

        /**
         * A stored PDF note goes straight through the store: its page repaints from
         * `selections` whenever it is on screen, so the reader is not taken there to save.
         */
        async saveStoredNote() {
            try {
                await this.updateStoredHighlight({
                    highlight: this.target,
                    color: this.selectedColor,
                    colorSlug: this.selectedColorSlug,
                    annotationText: this.annotation,
                    annotationFormat: this.annotationFormat,
                });
                this.closeNoteEditor();
            } catch (error) {
                this.isSaving = false;
                this.openToast({ text: error?.message || this.localizations[this.lang].highlightCreationFailed });
            }
        },

        /**
         * What the input opens with. The rich editor prefers the stored markup and
         * converts a legacy plain note into escaped paragraphs first, because handing
         * it raw text with newlines would collapse them into one line. The plain
         * textarea takes the text as it is.
         * @param {Object|null} highlight - The note being edited, or the selection a new one is written on
         * @returns {string}
         */
        annotationForEditor(highlight) {
            if (!this.richTextEditorIsEnabled) {
                return highlight?.annotationText || '';
            }

            return highlight?.annotationHtml || plainTextToEditorHtml(highlight?.annotationText || '');
        },

        /**
         * @param {string} sentence - The sentence dictation just finished, trailing space included
         */
        appendDictation(sentence) {
            if (typeof this.$refs.noteField?.insertText === 'function') {
                this.$refs.noteField.insertText(sentence);
                return;
            }

            this.annotation += sentence;
        },

        /**
         * Navigates to the note's page when its text layer is not mounted, then waits
         * for the layer to appear so it can receive the save.
         * @param {number} page - The page the note sits on
         * @returns {Promise<boolean>} False when the layer never mounts within PAGE_MOUNT_TIMEOUT_MS
         */
        async ensurePageTextLayerIsMounted(page) {
            const isMounted = () => Boolean(document.querySelector(`[data-text-layer-page="${page}"]`));
            if (isMounted()) return true;

            this.goToPage({ page, trigger: 'click', source: 'notes' });
            for (let waited = 0; waited < PAGE_MOUNT_TIMEOUT_MS; waited += PAGE_MOUNT_POLL_MS) {
                await new Promise(resolve => setTimeout(resolve, PAGE_MOUNT_POLL_MS));
                if (isMounted()) return true;
            }
            return false;
        },
    },
};
</script>

<style scoped>
/* Same break the highlighter form takes: the colors keep the line to themselves and
   Cancel/Save land under them. Measured on the row rather than the viewport because the
   panel holding it is resizable, with the phone's bottom sheet stacked outright. */
@container (max-width: 422px) {
    .note-commit-row__colors {
        @apply basis-full;
    }
}

@media screen and (max-width: 570px) {
    .note-commit-row__colors {
        @apply basis-full;
    }
}
</style>
